/*
 * ETag threading for the untyped editor shims (finding 8; route-contract row
 * "The route contract", Approved — ETag on reads, If-Match on updates).
 *
 * The typed routes carry a `version` ETag seam on their models; the untyped
 * BFF/Identity shims (users, permission sets, notification preferences —
 * ADR 0009) have none. This is the one place that remembers the ETag a GET
 * returned for a resource so the matching PUT/PATCH can send it back as
 * `If-Match`. A concurrent save by someone else changes the server's ETag, so
 * this client's remembered ETag is then stale and the update is refused with a
 * 412 — the "two people saving the same record → the second is refused" row.
 *
 * Keyed by the resource's canonical path (the GET URL without query string), so
 * the ETag captured when the editor opened a record is the one its save sends.
 * The ETag is OPAQUE (no PII). Removed per-resource when consumed is not
 * necessary — a later GET overwrites it with the current value.
 *
 * UI-free (lib/api). A plain module-level Map, not a store: nothing renders off
 * it; it is read only at the transport boundary.
 */

const etagByResource = new Map<string, string>();

/** Canonical resource key — the path without query string. */
function resourceKey(url: string): string {
  const q = url.indexOf('?');
  return q === -1 ? url : url.slice(0, q);
}

/** Remember the ETag a GET returned for a resource (no-op when absent). */
export function rememberEtag(url: string, etag: string | null): void {
  if (etag) etagByResource.set(resourceKey(url), etag);
}

/** The last-seen ETag for a resource, for the matching update's If-Match. */
export function etagFor(url: string): string | undefined {
  return etagByResource.get(resourceKey(url));
}

/** Test/teardown helper — forget all remembered ETags. */
export function clearEtags(): void {
  etagByResource.clear();
}
